/* The collection sorts and filters in place, as the sketch: the section is rendered again for the new address
   (Section Rendering API) and its list swapped in (the section's own stylesheet and script stay), and the address
   follows. «Vis flere» adds the next page under this one, and Back brings the shopper to the same place; a search
   page with the same nav.collection-pages under its .card-grid gets it by loading this module. Without JS
   the forms post and the link opens the next page. The filter panel: a price step becomes Shopify's from and to when
   the form is sent (a second press on a step takes it away), and «Vis N produkter» follows the choices, from
   sections/collection-count on the URL they make. */
// Shared private views use the same exact Shopify-versioned module as core; public pages stay unchanged.
const sharedAccessModuleUrl = document.getElementById('laley-shared-access-context')?.dataset.moduleUrl;
const { privateAccessUrl } = sharedAccessModuleUrl
  ? await import(sharedAccessModuleUrl)
  : { privateAccessUrl: (url) => url };
const collectionSection = () => document.querySelector('.shopify-section:has(.collection-bar)');
const panel = document.getElementById('panel');
const plain = (event) => !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
const say = (text) => {
  const status = document.querySelector('[data-collection-status]');
  if (status) status.textContent = text;
};

// Under 1024 px the row starts at the chosen chip (it may be far to the right on a phone).
function showChosen(root = document) {
  root.querySelectorAll('.collection-nav').forEach((row) => {
    const chosen = row.querySelector('[aria-current]');
    if (chosen && row.scrollWidth > row.clientWidth)
      row.scrollLeft = chosen.offsetLeft - row.offsetLeft - (row.clientWidth - chosen.offsetWidth) / 2;
  });
}
showChosen();

// Category links keep their normal clicks; a deliberate horizontal mouse gesture moves the overflowing row.
// Delegation also covers the fresh row returned by Shopify after sorting or filtering.
let chipDrag = null;
let draggedRow = null;
function endChipDrag(event) {
  const drag = chipDrag;
  if (!drag || (event?.pointerId !== undefined && event.pointerId !== drag.id)) return;
  chipDrag = null;
  drag.row.classList.remove('is-dragging');
  if (drag.row.hasPointerCapture(drag.id)) drag.row.releasePointerCapture(drag.id);
}
document.addEventListener('pointerdown', (event) => {
  endChipDrag();
  draggedRow = null;
  const row = event.target.closest('.collection-nav');
  if (!row || event.pointerType !== 'mouse' || event.button !== 0 || event.isPrimary === false || !plain(event)) return;
  if (row.scrollWidth <= row.clientWidth + 1) return;
  chipDrag = { row, id: event.pointerId, x: event.clientX, y: event.clientY, left: row.scrollLeft, moved: false };
});
document.addEventListener('pointermove', (event) => {
  const drag = chipDrag;
  if (!drag || event.pointerId !== drag.id) return;
  if (!drag.row.isConnected || !(event.buttons & 1)) return endChipDrag(event);
  const dx = event.clientX - drag.x;
  const dy = event.clientY - drag.y;
  if (!drag.moved) {
    if (Math.max(Math.abs(dx), Math.abs(dy)) <= 6) return;
    draggedRow = drag.row;
    // A vertical gesture is neither a horizontal slide nor a category click.
    if (Math.abs(dx) <= Math.abs(dy)) return endChipDrag(event);
    drag.moved = true;
    drag.row.classList.add('is-dragging');
    drag.row.setPointerCapture(drag.id);
  }
  event.preventDefault();
  drag.row.scrollLeft = drag.left - dx;
});
for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) document.addEventListener(type, endChipDrag);
document.addEventListener('pointerout', (event) => {
  // A press that leaves before capture must not wait forever for a release outside the row.
  if (chipDrag && !chipDrag.moved && !chipDrag.row.contains(event.relatedTarget)) endChipDrag(event);
});
document.addEventListener('dragstart', (event) => {
  if (event.target.closest('.collection-nav')) event.preventDefault();
});
document.addEventListener(
  'click',
  (event) => {
    if (event.detail > 0 && draggedRow && event.target.closest('.collection-nav') === draggedRow) {
      draggedRow = null;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true,
);
for (const type of ['blur', 'resize']) addEventListener(type, () => endChipDrag());
document.addEventListener('keydown', () => endChipDrag());
document.addEventListener('visibilitychange', () => {
  if (document.hidden) endChipDrag();
});

/** A section (the collection's by default) as rendered for an address. */
async function sectionAt(url, signal, section = collectionSection()) {
  url = privateAccessUrl(url);
  const id = section.id.replace('shopify-section-', '');
  const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${id}`, { signal });
  if (!response.ok) throw new Error(response.status);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

// «Vis flere» remembers, per address, the last page added and where the page stood, for Back.
const memoryKey = () => `more:${location.pathname}${location.search}`;
function remember(memory) {
  try {
    if (memory) sessionStorage.setItem(memoryKey(), JSON.stringify(memory));
    else sessionStorage.removeItem(memoryKey());
  } catch {
    /* no storage: Back shows the first page */
  }
}
function recall() {
  try {
    return JSON.parse(sessionStorage.getItem(memoryKey()));
  } catch {
    return null;
  }
}

// The address the section shows: only a change of it reloads on Back (a #fragment, like the skip link, does not).
let shown = location.pathname + location.search;
let swap;
function syncFilterSort(form) {
  const selected = collectionSection()?.querySelector('select[name=sort_by]');
  const kept = form.querySelector('input[name=sort_by]');
  if (selected && kept) kept.value = selected.value;
}
async function showCollection(form, { push }) {
  if (form.matches('form.filters')) syncFilterSort(form);
  const url = `${form.action}?${new URLSearchParams(new FormData(form))}`;
  swap?.abort();
  const request = (swap = new AbortController());
  const current = () => request === swap && !request.signal.aborted;
  let fresh;
  try {
    fresh = await sectionAt(url, request.signal);
  } catch (error) {
    if (current() && error.name !== 'AbortError') location.href = url;
    return null;
  }
  if (!current()) return null;
  const section = collectionSection();
  const list = fresh.querySelector('.collection');
  const filters = fresh.querySelector('template[data-view="filters"]');
  // Anything unexpected: the ordinary page load.
  if (!list || !filters) {
    location.href = url;
    return null;
  }
  remember(null);
  endChipDrag();
  draggedRow = null;
  section.querySelector('.collection').replaceWith(list);
  section.querySelector('template[data-view="filters"]').replaceWith(filters);
  window.Laley.enhancePanelLinks(section);
  showChosen(section);
  window.Laley.syncFavorites?.();
  history[push ? 'pushState' : 'replaceState'](null, '', url);
  shown = location.pathname + location.search;
  return section;
}

document.addEventListener('change', async (event) => {
  const form = event.target.closest('form[data-autosubmit]');
  if (!form) return;
  const order = event.target.selectedOptions?.[0]?.text.toLowerCase();
  const section = await showCollection(form, { push: false });
  if (!section) return;
  section.querySelector('select[name=sort_by]')?.focus({ preventScroll: true });
  const status = section.querySelector('[data-collection-status]');
  if (order && status) say(status.dataset.sorted.replace('[order]', order));
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form.filters');
  if (!form) return;
  event.preventDefault();
  const section = await showCollection(form, { push: true });
  if (!section) return;
  // The new list from its start: scrolled past the bar, the page goes back to where the bar sits under the header.
  const bar = section.querySelector('.collection-bar');
  // The block over the bar is the first one that renders (from 1024 px the main-menu row is display: none).
  let above = bar.previousElementSibling;
  while (!above.getClientRects().length) above = above.previousElementSibling;
  const top =
    above.getBoundingClientRect().bottom +
    scrollY +
    parseFloat(getComputedStyle(bar.parentElement).rowGap) -
    parseFloat(getComputedStyle(bar).top);
  if (scrollY > top) scrollTo(0, top);
  // Focus goes back to the new Filtrer, and the new count is said once the panel has closed.
  panel.addEventListener(
    'close',
    () => {
      section.querySelector('.collection-filter')?.focus({ preventScroll: true });
      say(section.querySelector('.collection-count')?.textContent ?? '');
    },
    { once: true },
  );
  window.Laley.closePanel();
});
addEventListener('popstate', () => {
  if (location.pathname + location.search !== shown) location.reload();
});

// The chip of the page itself does nothing; the row keeps a focused chip in view, clear of the screen's edges.
document.addEventListener('click', (event) => {
  if (plain(event) && event.target.closest('.collection-nav [aria-current="page"]')) event.preventDefault();
});
document.addEventListener('focusin', (event) => {
  const row = event.target.closest?.('.collection-nav');
  if (row && row !== chipDrag?.row) event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
});

/** Add the page at href under the list of the section that holds the pages (a collection's or the search's: a
    .card-grid and nav.collection-pages); gives the first new card. The list keeps its own start: the caption counts
    from its first product, and a list that began past page one keeps its «Forrige side». */
const pagesInFlight = new WeakMap();
function addPage(href, section) {
  const grid = section.querySelector('.card-grid');
  if (pagesInFlight.has(grid)) return pagesInFlight.get(grid);
  const more = section.querySelector('[data-more]');
  more?.setAttribute('aria-busy', 'true');
  const request = appendPage(href, section).finally(() => {
    pagesInFlight.delete(grid);
    more?.removeAttribute('aria-busy');
  });
  pagesInFlight.set(grid, request);
  return request;
}

async function appendPage(href, section) {
  const list = section.querySelector('.card-grid');
  const old = section.querySelector('.collection-pages');
  const fresh = await sectionAt(href, undefined, section);
  // A new sort or filter can replace the list while this request is on its way. That answer no longer belongs here.
  if (list !== section.querySelector('.card-grid')) return;
  const cards = fresh.querySelector('.card-grid');
  const pages = fresh.querySelector('.collection-pages');
  if (!cards || !pages) throw new Error('No page');
  const first = cards.firstElementChild;
  list.append(...cards.children);
  const back = old.querySelector('[data-previous]');
  const added = pages.querySelector('[data-previous]');
  if (back) added.replaceWith(back);
  else added?.remove();
  const caption = pages.querySelector('[data-showing]');
  const from = Number(old.querySelector('[data-from]')?.dataset.from || 1);
  if (caption) {
    caption.dataset.from = from;
    caption.textContent = from > 1 ? caption.dataset.range.replace('[from]', from) : caption.dataset.showing;
  }
  old.replaceWith(pages);
  // Search filters Shopify's matches, so it names the matches actually shown, never the unfiltered total.
  const matches = section.querySelector('[data-search-shown]');
  if (matches)
    matches.textContent = matches.dataset.searchShown.replace('#', section.querySelector('.card-grid').children.length);
  window.Laley.syncFavorites?.();
  return first;
}
const pageOf = (href) => Number(new URL(href, location.href).searchParams.get('page') || 1);

document.addEventListener('click', async (event) => {
  const more = event.target.closest('.collection-pages [data-more]');
  if (!more || !plain(event)) return;
  event.preventDefault();
  try {
    const first = await addPage(more.href, more.closest('.shopify-section'));
    if (first === undefined) return;
    first?.querySelector('.card-title')?.focus({ preventScroll: true });
    remember({ last: pageOf(more.href), y: scrollY });
  } catch {
    // A failed old request must not take the shopper away from a newer sort or filter either.
    if (more.isConnected) location.href = more.href;
  }
});
addEventListener('pagehide', () => {
  const memory = recall();
  if (memory) remember({ ...memory, y: scrollY });
});
// Back to a list that had more pages: they are added again, in order, and the page stands where it stood.
const memory = recall();
const listed = document.querySelector('.shopify-section:has(.collection-pages)');
if (
  performance.getEntriesByType('navigation')[0]?.type === 'back_forward' &&
  listed &&
  memory?.last > pageOf(location.href)
) {
  history.scrollRestoration = 'manual';
  const url = new URL(location.href);
  try {
    for (let page = pageOf(location.href) + 1; page <= memory.last; page += 1) {
      url.searchParams.set('page', page);
      if ((await addPage(url.href, listed)) === undefined) break;
    }
  } catch {
    /* the pages added so far stay */
  }
  scrollTo(0, memory.y);
} else remember(null);

// The form's data (sent, or read for the count): the step «400-799» as filter.v.price.gte=400 and .lte=799.
document.addEventListener('formdata', (event) => {
  const steps = event.target.querySelector?.('[data-price-steps]');
  if (!steps) return;
  const step = event.formData.get('price');
  event.formData.delete('price');
  if (!step) return;
  const [gte, lte] = step.split('-');
  if (Number(gte) > 0) event.formData.set(steps.dataset.gte, gte);
  if (lte) event.formData.set(steps.dataset.lte, lte);
});

// A press on the chosen price step (pointer or Space) takes it away. For Space the key-up does it, and keeps the
// browser from choosing the step again.
let wasChosen = false;
const priceStep = (target) => target.matches?.('[data-price-steps] input') && target;
function release(step) {
  wasChosen = false;
  step.checked = false;
  step.dispatchEvent(new Event('change', { bubbles: true }));
}
document.addEventListener('pointerdown', (event) => {
  wasChosen = !!event.target.closest('[data-price-steps] label')?.querySelector('input')?.checked;
});
document.addEventListener('keydown', (event) => {
  if (event.key === ' ' && priceStep(event.target)) wasChosen = event.target.checked;
});
document.addEventListener('click', (event) => {
  const step = priceStep(event.target);
  if (step && wasChosen) release(step);
});
document.addEventListener('keyup', (event) => {
  const step = event.key === ' ' && priceStep(event.target);
  if (!step || !wasChosen || !step.checked) return;
  event.preventDefault();
  release(step);
});

// Reset the choices being edited, including active filters kept as hidden fields. The sort stays as chosen.
const filterInputs = (form) =>
  [...form.querySelectorAll('input')].filter((input) => input.name === 'price' || input.name.startsWith('filter.'));
function syncFilterReset(form) {
  const reset = form.querySelector('[data-filter-reset]');
  if (reset)
    reset.disabled = !filterInputs(form).some(
      (input) => !input.disabled && (['checkbox', 'radio'].includes(input.type) ? input.checked : input.value !== ''),
    );
}

let counting;
async function countFilters(form) {
  syncFilterReset(form);
  counting?.abort();
  const request = (counting = new AbortController());
  const button = form.querySelector('[data-filter-show]');
  if (!button) return;
  // Applying still works while the optional count loads, and after a connection failure.
  button.textContent = button.dataset.defaultLabel;
  button.disabled = false;
  button.setAttribute('aria-busy', 'true');
  const params = new URLSearchParams(new FormData(form));
  params.set('section_id', 'collection-count');
  const current = () => form.isConnected && request === counting && !request.signal.aborted;
  try {
    const response = await fetch(`${form.action}?${params}`, { signal: request.signal });
    if (!response.ok) throw new Error(response.status);
    const html = await response.text();
    if (!current()) return;
    const raw = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-count]')?.dataset.count;
    const count = raw === undefined || raw === '' ? NaN : Number(raw);
    if (!Number.isInteger(count) || count < 0) return;
    button.textContent = (count === 1 ? button.dataset.one : button.dataset.other).replace('#', count);
    // Choices that give nothing cannot be shown.
    button.disabled = count === 0;
  } catch {
    // The ordinary submit remains available if the count cannot be fetched.
  } finally {
    if (current()) button.removeAttribute('aria-busy');
  }
}

document.addEventListener('change', (event) => {
  const form = event.target.closest('form.filters');
  if (form) countFilters(form);
});
document.addEventListener('click', (event) => {
  const reset = event.target.closest('[data-filter-reset]');
  if (!reset) return;
  const form = reset.closest('form.filters');
  if (!form) return;
  for (const input of filterInputs(form)) {
    if (['checkbox', 'radio'].includes(input.type)) input.checked = false;
    else {
      input.value = '';
      if (input.type === 'hidden') input.disabled = true;
    }
  }
  wasChosen = false;
  countFilters(form);
});
panel.addEventListener('panel:open', () => {
  counting?.abort();
  const form = panel.querySelector('form.filters');
  if (form) {
    // The shopper may open filters before a selected sort's section response replaces its old template.
    syncFilterSort(form);
    syncFilterReset(form);
  }
});
panel.addEventListener('close', () => counting?.abort());
